import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from '../sections/HeroSection';
import TrustBar from '../sections/TrustBar';
import ServicesOverview from '../sections/ServicesOverview';
import AIAnswerBlock from '../sections/AIAnswerBlock';
import ProcessSection from '../sections/ProcessSection';
import WhyChooseUs from '../sections/WhyChooseUs';
import ServiceAreas from '../sections/ServiceAreas';
import TestimonialsSection from '../sections/TestimonialsSection';
import FAQSection from '../sections/FAQSection';
import FinalCTA from '../sections/FinalCTA';
import type { HeroContent } from '../sections/HeroSection';
import type { TrustStat } from '../sections/TrustBar';
import type { ServicesContent } from '../sections/ServicesOverview';
import type { AIAnswerContent } from '../sections/AIAnswerBlock';
import type { ProcessContent } from '../sections/ProcessSection';
import type { WhyChooseUsContent } from '../sections/WhyChooseUs';
import type { ServiceAreasContent } from '../sections/ServiceAreas';
import type { TestimonialsContent } from '../sections/TestimonialsSection';
import type { FAQContent } from '../sections/FAQSection';
import type { FinalCTAContent } from '../sections/FinalCTA';

const heroContent: HeroContent = {
  eyebrow: 'Expert Drain & Sewer Solutions — San Bernardino, CA',
  headlineLines: [
    'Every Drain & Sewer',
    'Service Your Property',
    'Requires.',
  ],
  subheadline:
    'From emergency drain cleaning to full sewer line replacement — Drain and Sewer Pro delivers nine specialized services backed by California licensing, upfront pricing, and guaranteed workmanship throughout the Inland Empire.',
  primaryCTA: { text: 'Get Free Quote', link: '/location' },
  secondaryCTA: { text: 'Emergency Service', href: 'tel:+1-909-000-0000' },
  badges: [
    { icon: 'ri-water-flash-line', label: 'Drain Cleaning' },
    { icon: 'ri-git-branch-line', label: 'Sewer Repair' },
    { icon: 'ri-speed-line', label: 'Hydro Jetting' },
    { icon: 'ri-fire-line', label: 'Water Heaters' },
    { icon: 'ri-alarm-warning-line', label: '24/7 Emergency' },
  ],
};

const trustStats: TrustStat[] = [
  { value: '9', label: 'Drain & Sewer Services', icon: 'ri-tools-line', note: 'Residential & commercial' },
  { value: '500+', label: 'Verified 5-Star Reviews', icon: 'ri-star-fill', note: 'Google rated 4.9' },
  { value: '100%', label: 'Satisfaction Guarantee', icon: 'ri-shield-check-line', note: 'On every job' },
  { value: '24/7', label: 'Emergency Availability', icon: 'ri-alarm-warning-line', note: 'No extra charge to call' },
];

const servicesContent: ServicesContent = {
  eyebrow: 'Full-Service Plumbing',
  headline: 'Every Drain & Sewer Service You Need',
  subheadline: 'Advanced solutions backed by licensed technicians and professional-grade equipment.',
  services: [
    {
      icon: 'ri-water-flash-line',
      title: 'Drain Cleaning',
      bullets: [
        'Clogged sink, tub & floor drains',
        'Kitchen & bathroom drain clearing',
        'Recurring clog prevention service',
      ],
      link: '/services',
    },
    {
      icon: 'ri-git-branch-line',
      title: 'Sewer Line Repair',
      bullets: [
        'Full sewer diagnosis & repair',
        'Trenchless pipe lining (CIPP)',
        'Complete sewer replacement',
      ],
      link: '/services',
    },
    {
      icon: 'ri-speed-line',
      title: 'Hydro Jetting',
      bullets: [
        'High-pressure water jetting',
        'Grease, scale & root removal',
        'Preventive maintenance flushes',
      ],
      link: '/services',
    },
    {
      icon: 'ri-tools-line',
      title: 'Rooter Service',
      bullets: [
        'Root intrusion removal',
        'Mechanical augering & snaking',
        'Root barrier treatments',
      ],
      link: '/services',
    },
    {
      icon: 'ri-recycle-line',
      title: 'Sewer Liners (CIPP)',
      bullets: [
        'Cured-in-place pipe lining',
        'No-excavation pipe rehabilitation',
        'Extends pipe life by decades',
      ],
      link: '/services',
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Plumbing Repairs',
      bullets: [
        'Leak detection & repair',
        'Pipe restoration & replacement',
        'Emergency plumbing response',
      ],
      link: '/services',
    },
    {
      icon: 'ri-fire-line',
      title: 'Tankless Water Heaters',
      bullets: [
        'Tankless installation & repair',
        'Upgrade from tank-style systems',
        'Energy-efficient hot water solutions',
      ],
      link: '/services',
    },
    {
      icon: 'ri-building-4-line',
      title: 'Commercial Plumbing',
      bullets: [
        'Restaurant & retail drain service',
        'Multi-unit sewer solutions',
        'Commercial maintenance programs',
      ],
      link: '/services',
    },
    {
      icon: 'ri-temp-hot-line',
      title: 'Water Heater Service',
      bullets: [
        'Water heater repair & flush',
        'Tank unit repair & replacement',
        'Annual maintenance inspection',
      ],
      link: '/services',
    },
  ],
  ctaText: 'Request Free Estimate',
  ctaLink: '/location',
};

const aiContent: AIAnswerContent = {
  mainQuestion: 'What Drain & Sewer Services Does Drain and Sewer Pro Offer?',
  answer:
    'Drain and Sewer Pro offers nine comprehensive drain and sewer services in San Bernardino, CA: drain cleaning, sewer line repair and replacement, hydro jetting, rooter service, sewer liner (CIPP) installation, plumbing repairs, tankless water heater installation, commercial plumbing, and water heater service. All performed by California-licensed technicians with upfront pricing and guaranteed workmanship.',
  leftBlock: {
    title: 'Residential Services',
    items: [
      'Kitchen & bathroom drain cleaning',
      'Main line sewer clearing',
      'Video inspection & diagnosis',
      'Hydro jetting & rooter service',
      'Sewer line repair & lining',
      'Water heater service & repair',
    ],
  },
  rightBlock: {
    title: 'Commercial Services',
    items: [
      'Restaurant grease trap & drain cleaning',
      'Commercial hydro jetting',
      'Multi-unit sewer inspections',
      'Sewer line replacement',
      'Preventive maintenance programs',
      'Emergency commercial response',
    ],
  },
};

const processContent: ProcessContent = {
  eyebrow: 'Service Process',
  headline: 'From First Call to Final Guarantee',
  steps: [
    {
      number: '01',
      title: 'Video Inspection',
      description: 'HD camera diagnostics pinpoint the exact issue before we recommend or price any repair.',
    },
    {
      number: '02',
      title: 'Written Estimate',
      description: 'Clear, itemized written quote. Zero hidden fees — you approve before any work begins.',
    },
    {
      number: '03',
      title: 'Expert Repair',
      description: 'Licensed technicians execute using professional drain cleaning and sewer repair equipment.',
    },
    {
      number: '04',
      title: 'Verified Resolution',
      description: 'Post-service camera confirms the job is fully resolved. Written guarantee included.',
    },
  ],
};

const whyContent: WhyChooseUsContent = {
  subheadline: 'The Drain & Sewer Pro Difference',
  headline: 'Premium Service. Guaranteed Results.',
  body:
    'Other companies send whoever\'s available. Drain and Sewer Pro sends licensed drain and sewer specialists who diagnose correctly, repair with precision, and back every job with a written guarantee. That\'s the difference when you call the professionals.',
  ctaText: 'Book Your Service',
  ctaLink: '/location',
  features: [
    {
      icon: 'ri-camera-line',
      title: 'Video Diagnostics First',
      description: 'We never guess. Every diagnosis is camera-confirmed before we recommend or price work.',
    },
    {
      icon: 'ri-speed-line',
      title: 'Hydro Jetting Experts',
      description: 'Professional-grade hydro jetting clears what basic snaking cannot — guaranteed.',
    },
    {
      icon: 'ri-recycle-line',
      title: 'Trenchless CIPP Lining',
      description: 'Sewer rehabilitation without excavation — protects your landscaping and foundation.',
    },
    {
      icon: 'ri-time-line',
      title: 'Same-Day Availability',
      description: 'Most service calls scheduled same day. Emergency calls dispatched immediately.',
    },
    {
      icon: 'ri-fire-line',
      title: 'Tankless Water Heater Pros',
      description: 'Installation, repair, and upgrades for all major tankless water heater brands.',
    },
    {
      icon: 'ri-award-line',
      title: 'Results Guaranteed',
      description: 'If the drain isn\'t fully clear and flowing, we return at no additional charge.',
    },
  ],
};

const areasContent: ServiceAreasContent = {
  eyebrow: 'Where We Work',
  headline: 'Drain & Sewer Services Across San Bernardino County',
  description:
    'Licensed drain and sewer services throughout the Inland Empire — residential and commercial.',
  areas: [
    'San Bernardino',
    'Riverside',
    'Fontana',
    'Moreno Valley',
    'Rancho Cucamonga',
    'Rialto',
    'Redlands',
    'Highland',
    'Colton',
    'Loma Linda',
    'Upland',
  ],
  note: 'Contact us to confirm same-day availability in your specific area. Emergency drain and sewer service available 24/7.',
  mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52833.65!2d-117.2882!3d34.1538!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c35c9a6d000001%3A0x1!2s4892+N+Pershing+Ave%2C+San+Bernardino%2C+CA+92407!5e0!3m2!1sen!2sus!4v1711000000001!5m2!1sen!2sus',
};

const testimonialsContent: TestimonialsContent = {
  eyebrow: 'Client Reviews',
  headline: 'What Clients Say About Our Services',
  overallRating: '4.9',
  reviewCount: '500+',
  testimonials: [
    {
      text: 'I had a completely blocked main sewer line on a Friday afternoon. Drain and Sewer Pro arrived within 90 minutes, ran a camera inspection, used the hydro jet, and had everything flowing perfectly. Exceptional, professional service from start to finish.',
      name: 'David M.',
      location: 'San Bernardino, CA',
      rating: 5,
    },
    {
      text: 'Their video camera found a root intrusion that had been slowing our kitchen drain for months. No other company had located it. Fixed properly the same day. Truly professional drain and sewer experts — I recommend them to everyone.',
      name: 'Lisa P.',
      location: 'Fontana, CA',
      rating: 5,
    },
    {
      text: 'We used CIPP sewer lining on an older property. The trenchless repair saved our landscaping entirely. The team was meticulous, communicative, and clean throughout the entire process. Absolutely worth every penny.',
      name: 'Carlos & Ana V.',
      location: 'Redlands, CA',
      rating: 5,
    },
  ],
};

const faqContent: FAQContent = {
  eyebrow: 'Service FAQs',
  headline: 'Drain & Sewer Service Questions',
  faqs: [
    {
      question: 'How much does drain cleaning cost in San Bernardino, CA?',
      answer:
        'Drain cleaning costs vary by drain type, severity, and method required. Drain and Sewer Pro provides free on-site estimates so you know the exact cost before any work begins. We offer transparent, written quotes with no hidden fees or surprise charges on your final invoice.',
    },
    {
      question: 'How long does sewer line repair take?',
      answer:
        'Timeline depends on damage extent and repair method. Traditional repairs may take 1 to 3 days. Trenchless CIPP sewer lining is often completed in a single day. We provide a realistic timeline during your free on-site estimate before scheduling any drain or sewer repair work.',
    },
    {
      question: 'Do you offer emergency drain service near me?',
      answer:
        'Yes. Drain and Sewer Pro provides 24/7 emergency drain and sewer service throughout San Bernardino, Riverside, Fontana, and all surrounding communities. Drain emergencies don\'t wait for business hours — our licensed technicians are always on call and ready to dispatch.',
    },
    {
      question: 'What causes sewer line backups and drain clogs?',
      answer:
        'Common causes include grease buildup, hair, root intrusion, pipe deterioration, and foreign objects. Drain and Sewer Pro uses HD video camera inspection to identify the exact cause before recommending any drain cleaning or sewer repair — ensuring we fix the real problem, not just a symptom.',
    },
    {
      question: 'Do you install and repair tankless water heaters?',
      answer:
        'Yes. Drain and Sewer Pro installs and services tankless water heaters throughout San Bernardino County. We handle new installations, repairs, and upgrades from conventional tank systems. All work is performed by CA-licensed technicians with upfront pricing and a written guarantee.',
    },
    {
      question: 'Do you guarantee your drain and sewer work?',
      answer:
        'Yes. Every service Drain and Sewer Pro performs is backed by a written workmanship guarantee. If the issue recurs within the guarantee period due to our workmanship, we return at no additional cost. Our goal is complete resolution on the first visit — every single time.',
    },
  ],
};

const finalCTAContent: FinalCTAContent = {
  eyebrow: 'Get Started Today',
  headlineLines: ['Need Drain or Sewer', 'Service Today?'],
  subheadline:
    'Fast response, upfront pricing, and guaranteed results — that\'s the Drain and Sewer Pro standard for every job.',
  primaryCTA: { text: 'Get Free Quote', link: '/location' },
  secondaryCTA: { text: 'Emergency Call Now', href: 'tel:+1-909-000-0000' },
};

const ServicesPage = () => (
  <div className="w-full">
    <Navbar />
    <main>
      <HeroSection content={heroContent} />
      <TrustBar stats={trustStats} />
      <ServicesOverview content={servicesContent} />
      <AIAnswerBlock content={aiContent} />
      <ProcessSection content={processContent} />
      <WhyChooseUs content={whyContent} />
      <ServiceAreas content={areasContent} />
      <TestimonialsSection content={testimonialsContent} />
      <FAQSection content={faqContent} />
      <FinalCTA content={finalCTAContent} />
    </main>
    <Footer />
  </div>
);

export default ServicesPage;
